import * as React from 'react'
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronsLeftIcon,
    ChevronsRightIcon,
    MoreHorizontalIcon,
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'

function Pagination({ className, ...props }) {
    return (
        <nav
            role="navigation"
            aria-label="pagination"
            data-slot="pagination"
            className={cn('tw:mx-auto tw:flex tw:w-full tw:justify-center', className)}
            {...props}
        />
    )
}

function PaginationContent({ className, ...props }) {
    return (
        <ul
            data-slot="pagination-content"
            className={cn('tw:flex tw:flex-row tw:items-center tw:gap-1', className)}
            {...props}
        />
    )
}

function PaginationItem({ ...props }) {
    return <li data-slot="pagination-item" {...props} />
}

function PaginationLink({ className, isActive, size = 'icon', ...props }) {
    return (
        <a
            aria-current={isActive ? 'page' : undefined}
            data-slot="pagination-link"
            data-active={isActive}
            className={cn(
                buttonVariants({
                    variant: isActive ? 'secondary' : 'ghost',
                    size,
                }),
                className,
            )}
            {...props}
        />
    )
}

function PaginationFirst({ className, showText = true, text = 'First', ...props }) {
    return (
        <PaginationLink
            aria-label="Go to first page"
            size="default"
            className={cn('tw:gap-1 tw:!p-2.5', className)}
            {...props}
        >
            <ChevronsLeftIcon className="tw:size-4" />
            {showText && <span className="tw:hidden tw:sm:block">{text}</span>}
        </PaginationLink>
    )
}

function PaginationPrevious({ className, showText = true, text = 'Previous', ...props }) {
    return (
        <PaginationLink
            aria-label="Go to previous page"
            size="default"
            className={cn('tw:gap-1 tw:!p-2.5', className)}
            {...props}
        >
            <ChevronLeftIcon className="tw:size-4" />
            {showText && <span className="tw:hidden tw:sm:block">{text}</span>}
        </PaginationLink>
    )
}

function PaginationNext({ className, showText = true, text = 'Next', ...props }) {
    return (
        <PaginationLink
            aria-label="Go to next page"
            size="default"
            className={cn('tw:gap-1 tw:!p-2.5', className)}
            {...props}
        >
            {showText && <span className="tw:hidden tw:sm:block">{text}</span>}
            <ChevronRightIcon className="tw:size-4" />
        </PaginationLink>
    )
}

function PaginationLast({ className, showText = true, text = 'Last', ...props }) {
    return (
        <PaginationLink
            aria-label="Go to last page"
            size="default"
            className={cn('tw:gap-1 tw:!p-2.5', className)}
            {...props}
        >
            {showText && <span className="tw:hidden tw:sm:block">{text}</span>}
            <ChevronsRightIcon className="tw:size-4" />
        </PaginationLink>
    )
}

function PaginationEllipsis({ className, ...props }) {
    return (
        <span
            aria-hidden
            data-slot="pagination-ellipsis"
            className={cn('tw:flex tw:size-9 tw:items-center tw:justify-center', className)}
            {...props}
        >
            <MoreHorizontalIcon className="tw:size-4" />
            <span className="tw:sr-only">More pages</span>
        </span>
    )
}

export {
    Pagination,
    PaginationContent,
    PaginationLink,
    PaginationItem,
    PaginationFirst,
    PaginationPrevious,
    PaginationNext,
    PaginationLast,
    PaginationEllipsis,
}
